import type { MentionTarget } from "@serophin/shared";
import type { ComponentChildren, Ref } from "preact";
import { forwardRef } from "preact/compat";
import { useId, useRef, useState } from "preact/hooks";
import { EditorSurface, LivePreview, MentionPopupOverlay } from "./MarkdownEditorSurface";
import type { Selection } from "./markdownCommands";
import { createEditorHandlers } from "./markdownEditorHandlers";
import { type MarkdownEditorTheme, resolveMarkdownEditorTheme } from "./markdownEditorTheme";
import { buildToolbarCommands, type EditorMode, ModeToggle, Toolbar, type ToolbarCommand } from "./markdownEditorToolbar";
import { useMentionPopup } from "./useMentionPopup";

type MarkdownEditorProps = {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  ariaLabel?: string;
  error?: string;
  placeholder?: string;
  rows?: number;
  minRows?: number;
  autoFocus?: boolean;
  disabled?: boolean;
  onSubmit?: () => void;
  footer?: ComponentChildren;
  class?: string;
  theme?: Partial<MarkdownEditorTheme>;
  mentions?: MentionTarget[];
  livePreview?: boolean;
};

const assignRef = (ref: Ref<HTMLTextAreaElement> | undefined, node: HTMLTextAreaElement | null): void => {
  if (typeof ref === "function") {
    ref(node);
  } else if (ref) {
    (ref as { current: HTMLTextAreaElement | null }).current = node;
  }
};

const restoreSelection = (textarea: HTMLTextAreaElement | null, selection: Selection): void => {
  if (!textarea) {
    return;
  }
  textarea.focus();
  textarea.setSelectionRange(selection.start, selection.end);
};

const useMarkdownEditor = (
  value: string,
  onChange: (value: string) => void,
  forwardedRef: Ref<HTMLTextAreaElement> | undefined,
  onSubmit: (() => void) | undefined,
  mentions: MentionTarget[] | undefined,
) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const setTextareaNode = (node: HTMLTextAreaElement | null) => {
    textareaRef.current = node;
    assignRef(forwardedRef, node);
  };

  const currentSelection = (): Selection => {
    const textarea = textareaRef.current;
    return { start: textarea?.selectionStart ?? value.length, end: textarea?.selectionEnd ?? value.length };
  };

  const applyResult = (result: { value: string; selection: Selection }) => {
    onChange(result.value);
    requestAnimationFrame(() => restoreSelection(textareaRef.current, result.selection));
  };

  const runCommand = (command: ToolbarCommand) => applyResult(command.run(value, currentSelection()));
  const popup = useMentionPopup(value, onChange, mentions, textareaRef, (selection) => restoreSelection(textareaRef.current, selection));
  const handlers = createEditorHandlers({ value, onChange, onSubmit, currentSelection, applyResult, popup });

  return {
    setTextareaNode,
    runCommand,
    popup,
    handleInput: handlers.handleInput,
    handleKeyDown: handlers.handleKeyDown,
    handleKeyUp: (event: KeyboardEvent) => handlers.handleKeyUp(event, textareaRef.current),
    handleClick: () => handlers.handleClick(textareaRef.current),
  };
};

const EditorLabel = ({ id, label, theme }: { id: string; label: string | undefined; theme: MarkdownEditorTheme }) =>
  label ? (
    <label htmlFor={id} class={theme.label}>
      {label}
    </label>
  ) : null;

const EditorError = ({ id, error, theme }: { id: string; error: string | undefined; theme: MarkdownEditorTheme }) =>
  error ? (
    <p id={id} class={theme.error}>
      {error}
    </p>
  ) : null;

export const MarkdownEditor = forwardRef<HTMLTextAreaElement, MarkdownEditorProps>(
  (
    {
      value,
      onChange,
      label,
      ariaLabel,
      error,
      placeholder,
      rows,
      minRows,
      autoFocus,
      disabled,
      onSubmit,
      footer,
      class: className,
      theme: themeOverrides,
      mentions,
      livePreview,
    },
    ref,
  ) => {
    const theme = resolveMarkdownEditorTheme(themeOverrides);
    const [mode, setMode] = useState<EditorMode>("write");
    const activeMode: EditorMode = livePreview ? "write" : mode;
    const editor = useMarkdownEditor(value, onChange, ref, onSubmit, mentions);
    const editorId = useId();
    const errorId = `${editorId}-error`;

    return (
      <div class={`w-full ${className ?? ""}`.trim()}>
        <EditorLabel id={editorId} label={label} theme={theme} />
        <div class="flex flex-wrap items-center justify-between gap-1 mb-1">
          <Toolbar commands={buildToolbarCommands(16)} theme={theme} disabled={disabled || activeMode === "preview"} onRun={editor.runCommand} />
          {!livePreview && <ModeToggle mode={mode} theme={theme} onChange={setMode} />}
        </div>
        <div ref={editor.popup.wrapperRef} class="relative">
          <EditorSurface
            mode={activeMode}
            value={value}
            theme={theme}
            editorId={editorId}
            ariaLabel={label ? undefined : ariaLabel}
            errorId={errorId}
            placeholder={placeholder}
            rows={rows}
            minRows={minRows}
            autoFocus={autoFocus}
            disabled={disabled}
            error={error}
            mentions={mentions}
            setTextareaNode={editor.setTextareaNode}
            onInput={editor.handleInput}
            onKeyDown={editor.handleKeyDown}
            onKeyUp={editor.handleKeyUp}
            onClick={editor.handleClick}
          />
          <MentionPopupOverlay popup={editor.popup} theme={theme} />
        </div>
        {livePreview && <LivePreview value={value} theme={theme} mentions={mentions} />}
        <EditorError id={errorId} error={error} theme={theme} />
        {footer}
      </div>
    );
  },
);
